import {history, historyKeymap} from '@codemirror/commands'
import {Prec} from '@codemirror/state'
import {EditorView, keymap, placeholder, type ViewUpdate} from '@codemirror/view'
import {readOnly} from '@github-ui/code-mirror/extensions/readonly'
import {spacingControls} from '@github-ui/code-mirror/extensions/spacing-controls'
import {useTrackingRef} from '@github-ui/use-tracking-ref'
import {clsx} from 'clsx'
import {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react'

import {decorateMarkdownExtension} from './codemirror/extensions/decorate-markdown'
import {singleLineEditorDefaultKeymap, singleLineEditorExtension} from './codemirror/extensions/single-line-editor'
import {useCompartment} from './codemirror/hooks/use-compartment'
import styles from './HybridMarkdownEditor.module.css'
import {type MarkdownViewMode, ViewSwitch} from './ViewSwitch'

export interface HybridMarkdownEditorProps {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  onChangeViewMode?: (newViewMode: MarkdownViewMode) => void
  placeholder?: string
  'aria-describedby'?: string
}

export interface HybridMarkdownEditorRef {
  view: EditorView | null
}

export const HybridMarkdownEditor = forwardRef<HybridMarkdownEditorRef, HybridMarkdownEditorProps>(
  function HybridMarkdownEditor(
    {value, onChange, disabled, onChangeViewMode, placeholder: placeholderText, 'aria-describedby': ariaDescribedBy},
    ref,
  ) {
    /** Stable tracking reference to `onChange` so we don't have to update editor extensions when the prop changes. */
    const onChangeRef = useTrackingRef(onChange)
    const onUpdate = useCallback(
      (update: ViewUpdate) => {
        if (update.docChanged) onChangeRef.current(update.state.doc.toString())
      },
      [onChangeRef],
    )

    const [view, setView] = useState<EditorView | null>(null)
    useImperativeHandle(ref, () => ({view}), [view])

    // Each extension that depends on prop values must be contained in a compartment to avoid reinitializing the editor
    // when the props change. Compartments are constant references so safe to use as dependencies.
    const placeholderCompartment = useCompartment(
      view,
      useMemo(() => placeholder(placeholderText ?? ''), [placeholderText]),
    )

    const readonlyCompartment = useCompartment(
      view,
      useMemo(() => (disabled ? readOnly() : []), [disabled]),
    )

    const contentAttributesCompartment = useCompartment(
      view,
      useMemo(
        () =>
          EditorView.contentAttributes.of({
            'aria-label': 'Markdown content',
            ...(ariaDescribedBy ? {'aria-describedby': ariaDescribedBy} : {}),
            class: clsx(styles.content, 'markdown-body'),
          }),
        [ariaDescribedBy],
      ),
    )

    const extensions = useMemo(
      () => [
        Prec.highest(keymap.of(singleLineEditorDefaultKeymap)),
        singleLineEditorExtension,
        decorateMarkdownExtension,
        history(),
        EditorView.updateListener.of(onUpdate),
        contentAttributesCompartment,
        spacingControls({
          indentUnit: 2,
          indentWithTabs: false,
          lineWrapping: true,
        }),
        keymap.of([...historyKeymap]),
        placeholderCompartment,
        readonlyCompartment,
      ],
      // IMPORTANT: These dependencies should not change during the lifecycle of the component
      [onUpdate, placeholderCompartment, readonlyCompartment, contentAttributesCompartment],
    )

    const containerRef = useRef<HTMLDivElement>(null)

    const initialValueRef = useRef(value)
    useEffect(
      function initializeEditor() {
        const editorView = new EditorView({
          parent: containerRef.current!,
          doc: initialValueRef.current,
          extensions,
        })

        setView(editorView)

        return () => {
          editorView.destroy()
          setView(null)
        }
      },
      // IMPORTANT: These dependencies should not change during the lifecycle of the component
      [extensions],
    )

    useEffect(function syncValue() {
      if (view?.state.doc.toString() !== value)
        view?.update([view.state.update({changes: {from: 0, to: view.state.doc.length, insert: value}})])
      // Note no dependencies: this runs on every render intentionally. If we had [view, value] as deps, the effect
      // would not run if the user makes a change and then the parent re-renders with the same value (discarding the change).
    })

    return (
      <div className={styles.container}>
        <div className={styles.toolbar}>
          <div className={styles.viewSwitch}>
            <ViewSwitch selectedView="hybrid" onViewSelect={onChangeViewMode} />
          </div>
          <div className={styles.formattingTools} />
        </div>

        <div ref={containerRef} />
      </div>
    )
  },
)

try{ HybridMarkdownEditor.displayName ||= 'HybridMarkdownEditor' } catch {}
try{ HybridMarkdownEditor.displayName ||= 'HybridMarkdownEditor' } catch {}